import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity as ActivityIcon } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { ActivityItem, SectionLoading } from '@/components/community/ui'
import { getActivity, type ActivityResponse } from '@/services/communityApi'

const PAGE_SIZE = 20

export default function CommunityActivityPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [items, setItems] = useState<ActivityResponse[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)

  const load = useCallback((p: number) => {
    if (!community) return
    setLoading(true)
    getActivity(community.id, p, PAGE_SIZE)
      .then((res) => { setItems(res.content); setTotalPages(res.totalPages); setPage(p) })
      .catch(() => { setItems([]); setTotalPages(0) })
      .finally(() => setLoading(false))
  }, [community])

  useEffect(() => { load(0) }, [load])

  if (!community) return null

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.activities.title')} subtitle={t('community.activities.subtitle')} icon={ActivityIcon} />

      {loading ? <SectionLoading rows={5} /> : items.length === 0 ? (
        <GovEmptyState icon="📊" title={t('community.activities.noActivities')} />
      ) : (
        <GovCard>
          <ul className="divide-y divide-gray-100">
            {items.map((a) => <li key={a.id}><ActivityItem activity={a} /></li>)}
          </ul>
        </GovCard>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <GovButton variant="secondary" size="sm" disabled={page === 0 || loading} onClick={() => load(page - 1)}>
            {t('community.actions.prev')}
          </GovButton>
          <span className="text-sm text-gray-500">{page + 1} / {totalPages}</span>
          <GovButton variant="secondary" size="sm" disabled={page >= totalPages - 1 || loading} onClick={() => load(page + 1)}>
            {t('community.actions.next')}
          </GovButton>
        </div>
      )}
    </CommunityLayout>
  )
}